import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import Loader from "../../common/loader";
import CustomPagination from "../../common/custompagination";
import Sorting from "../../common/sorting";
import CustomRangepicker from "../../common/rangepicker";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import {
  formateDateWithMonth,
  handleServerValidations,
  globalLoader,
} from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import * as withdrawalServices from "../../services/withdrawalServices";
import { useTranslation } from "react-i18next";

const WithdrawalList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [list, setList] = useState([]);
  const [datalength, setDataLength] = useState(0);
  const [itemperpage] = useState(10);
  const [sorting, setSorting] = useState({});
  const [defaultSorting, setDefaultSorting] = useState(true);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [search, setSearch] = useState({});
  const [globalsearch, setGlobalSearch] = useState("");
  const [resetdate, setResetDate] = useState(false);
  const [statsupdate, setStatsUpdate] = useState(Date.now());

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "Transfer Requests", url: "" },
  ];

  // Fetch list
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoader(true);
      const formData = new FormData();
      formData.append("page", page);
      formData.append("per_page", itemperpage);
      formData.append("sort", JSON.stringify(sorting));
      formData.append("search", JSON.stringify(search));
      formData.append("global_search", globalsearch);

      withdrawalServices
        .List(formData)
        .then((response) => {
          if (response?.success) {
            setList(response.data.list || []);
            setDataLength(response.data.total_records || 0);
          }
          setLoader(false);
        })
        .catch((err) => {
          console.log("Error fetching withdrawals:", err);
          setLoader(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [page, sorting, search, globalsearch, statsupdate]);

  const handleSort = (column) => {
    setDefaultSorting(false);
    let sortObj = { order: 1, column: column };
    if (sorting.column === column) {
      sortObj.order = sorting.order === 1 ? -1 : 1;
    }
    setSorting(sortObj);
  };

  const prepareSearch = (key, value) => {
    let sr = { ...search };
    if (String(value).length > 0) {
      sr[key] = value;
    } else {
      delete sr[key];
    }
    setSearch(sr);
  };

  const resetFilter = (e) => {
    e.preventDefault();
    setGlobalSearch("");
    setSearch({});
    setResetDate(!resetdate);
    const statusSelect = document.getElementById("statusFilter");
    if (statusSelect) statusSelect.selectedIndex = 0;
  };

  // Show Bank/UPI Details Modal
  const showBankDetails = (withdrawal) => {
    const { bank_details, method, amount, astrologer_details: astrologer } = withdrawal;
    const isBank = method === "bank";
    const currency = astrologer?.astrologer_wallet?.currency_type === 'USD' ? '$' : '₹';

    let detailsHTML = isBank
      ? `
        <div class="text-start mt-3">
          <p><strong>Holder Name:</strong> ${bank_details.holder_name || "N/A"}</p>
          <p><strong>Account Number:</strong> <code>${bank_details.account_number || "N/A"}</code></p>
          <p><strong>IFSC Code:</strong> <code>${bank_details.ifsc_code || "N/A"}</code></p>
        </div>
      `
      : `
        <div class="text-start mt-3">
          <p><strong>UPI ID:</strong> <code>${bank_details.upi_id || "N/A"}</code></p>
          <p><strong>Name:</strong> ${bank_details.holder_name || "N/A"}</p>
        </div>
      `;

    Swal.fire({
      title: `<strong>${astrologer.name}</strong> • ${method.toUpperCase()} Details`,
      html: `
        <div class="text-center mb-3">
          <h4 class="text-primary">${currency}${amount.toLocaleString(currency === '$' ? "en-US" : "en-IN")}</h4>
        </div>
        ${detailsHTML}
        <div class="mt-4 text-center">
          ${isBank
            ? `
              <button id="copyAcc" class="btn btn-outline-primary btn-sm me-2">Copy A/c No</button>
              <button id="copyIfsc" class="btn btn-outline-primary btn-sm">Copy IFSC</button>
            `
            : `<button id="copyUpi" class="btn btn-outline-success btn-sm">Copy UPI ID</button>`
          }
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: "Close",
      width: "520px",
      customClass: { popup: "swal-wide" },
      didOpen: () => {
        if (isBank) {
          document.getElementById("copyAcc")?.addEventListener("click", () => {
            navigator.clipboard.writeText(bank_details.account_number);
            Swal.fire("Copied!", "Account number copied", "success");
          });
          document.getElementById("copyIfsc")?.addEventListener("click", () => {
            navigator.clipboard.writeText(bank_details.ifsc_code);
            Swal.fire("Copied!", "IFSC copied", "success");
          });
        } else {
          document.getElementById("copyUpi")?.addEventListener("click", () => {
            navigator.clipboard.writeText(bank_details.upi_id);
            Swal.fire("Copied!", "UPI ID copied", "success");
          });
        }
      },
    });
  };

  // Approve Transfer
  const approveWithdrawal = (withdrawalId) => {
    Swal.fire({
      title: "Approve Transfer?",
      text: "Enter UTR / Transaction ID after payment",
      icon: "question",
      input: "text",
      inputLabel: "UTR / Reference ID",
      inputPlaceholder: "e.g. SBIN1234567890",
      showCancelButton: true,
      confirmButtonText: "Approve & Save",
      preConfirm: (utr) => {
        if (!utr?.trim()) {
          Swal.showValidationMessage("UTR is required");
        }
        return utr;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("withdrawal_id", withdrawalId);
        formData.append("payment_id", result.value);
        formData.append("remarks", "Approved by admin");

        globalLoader(true);
        withdrawalServices
          .Approve(formData)
          .then((res) => {
            globalLoader(false);
            if (res.success) {
              Swal.fire("Success!", res.message, "success");
              setStatsUpdate(Date.now());
            } else {
              Swal.fire("Error", handleServerValidations(res), "error");
            }
          })
          .catch(() => {
            globalLoader(false);
            Swal.fire("Error", "Failed to approve", "error");
          });
      }
    });
  };

  // Reject Transfer
  const rejectWithdrawal = (withdrawalId) => {
    Swal.fire({
      title: "Reject Transfer?",
      input: "text",
      inputLabel: "Reason for rejection",
      inputPlaceholder: "e.g. Invalid bank details",
      showCancelButton: true,
      confirmButtonText: "Reject",
      preConfirm: (reason) => {
        if (!reason?.trim()) {
          Swal.showValidationMessage("Reason is required");
        }
        return reason;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("withdrawal_id", withdrawalId);
        formData.append("reason", result.value);

        globalLoader(true);
        withdrawalServices
          .Reject(formData)
          .then((res) => {
            globalLoader(false);
            if (res.success) {
              Swal.fire("Rejected", res.message, "success");
              setStatsUpdate(Date.now());
            } else {
              Swal.fire("Error", handleServerValidations(res), "error");
            }
          })
          .catch(() => {
            globalLoader(false);
            Swal.fire("Error", "Failed to reject", "error");
          });
      }
    });
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />

      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label mb-0">Transfer Requests</h6>

              <div className="d-flex align-items-center gap-2">
                {/* Global Search */}
                <div className="form-group mb-0">
                  <div className="form-group mb-0 rangepicker_container filter_design">
                    <i className="fa fa-search calender_icon"></i>
                    <input
                      type="search"
                      className="form-control"
                      value={globalsearch}
                      onChange={(e) => setGlobalSearch(e.target.value)}
                      placeholder="Search "
                    />
                  </div>
                </div>

                {/* Date Range */}
                <div>
                  <CustomRangepicker
                    GetDateRange={(e) => prepareSearch("createdAt", e)}
                    resetdate={resetdate}
                  />
                </div>

                {/* Status Filter */}
                <div className="form-group mb-0 filter_icon_container filter_design">
                  <i className="fa fa-filter mr-2 filter_icon"></i>
                  <div className="select-down-arrow">
                    <select
                      className="form-control cp"
                      id="statusFilter"
                      onChange={(e) => prepareSearch("status", e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <button className="btn btn-warning btn-sm" onClick={resetFilter}>
                  Reset
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered text-nowrap w-100">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>
                      <div className="sorting_column">
                        <span>Astrologer</span>
                        <Sorting sort={sorting} handleSort={() => handleSort("astrologer.name")} />
                      </div>
                    </th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>UTR / Ref</th>
                    <th>
                      <div className="sorting_column">
                        <span>Request Date</span>
                        <Sorting
                          sort={sorting}
                          handleSort={() => handleSort("createdAt")}
                          defaultSorting={defaultSorting}
                        />
                      </div>
                    </th>
                    <th className="action_head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        <Loader />
                      </td>
                    </tr>
                  ) : list.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No transfer requests found
                      </td>
                    </tr>
                  ) : (
                    list.map((row, i) => (
                      <tr key={row._id}>
                        <td>{(page - 1) * itemperpage + i + 1}</td>
                        <td>
                          <strong>{row.astrologer_details?.name || "N/A"}</strong>
                          <br />
                          <small className="text-muted">
                            Balance: {row.astrologer_details?.astrologer_wallet?.currency_type === 'USD' ? '$' : '₹'}{row.astrologer_details?.astrologer_wallet?.balance?.toLocaleString(row.astrologer_details?.astrologer_wallet?.currency_type === 'USD' ? "en-US" : "en-IN")}
                          </small>
                        </td>
                        <td>
                          <strong>{row.astrologer_details?.astrologer_wallet?.currency_type === 'USD' ? '$' : '₹'}{row.amount?.toLocaleString(row.astrologer_details?.astrologer_wallet?.currency_type === 'USD' ? "en-US" : "en-IN")}</strong>
                        </td>
                        <td>
                          <span
                            className={`badge ${row.method === "bank" ? "bg-primary" : "bg-success"}`}
                          >
                            {row.method?.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          {row.status === "pending" && (
                            <span className="badge bg-warning text-dark">Pending</span>
                          )}
                          {row.status === "approved" && (
                            <span className="badge bg-success">Approved</span>
                          )}
                          {row.status === "rejected" && (
                            <span className="badge bg-danger">Rejected</span>
                          )}
                          {row.status === "cancelled" && (
                            <span className="badge bg-secondary">Cancelled</span>
                          )}
                        </td>
                        <td>{row.payment_id || <em className="text-muted">—</em>}</td>
                        <td>{formateDateWithMonth(row.createdAt)}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-outline-info btn-sm me-1"
                            onClick={() => showBankDetails(row)}
                            title="View Bank Details"
                          >
                            Bank Details
                          </button>
                          {row.status === "pending" ? (
                            <>
                              <button
                                className="btn btn-success btn-sm me-1"
                                onClick={() => approveWithdrawal(row._id)}
                              >
                                Approve
                              </button>

                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => rejectWithdrawal(row._id)}
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            row.status !== "cancelled" && <span className="badge bg-light text-dark">Processed</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3">
              <b>Total Requests: {datalength}</b>
            </div>

            {datalength > itemperpage && (
              <CustomPagination
                datalength={datalength}
                itemperpage={itemperpage}
                setPage={setPage}
                currentPage={page}
                pageRoute={[{ name: "Transfers", path: "/admin/withdrawals/list" }]}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WithdrawalList;